/**
 * Short Owen reactions after each apply answer — sidebar always, chat only when substantive.
 */

const GENERIC_INSIGHTS = new Set([
  "Got it — I'm updating your program match.",
  'Recorded — narrowing your product match.',
  'File detail saved — readiness is climbing.',
  'That helps narrow the list.',
  'Noted — adjusting your program shortlist.',
  'Useful — one more filter applied.',
]);

const VARIED_SIDEBAR_ACKS = [
  'That helps narrow the list.',
  'Noted — adjusting your program shortlist.',
  'Useful — one more filter applied.',
  'Got it — that refines your match.',
];

function hashPick(seed, options) {
  let h = 0;
  const s = String(seed);
  for (let i = 0; i < s.length; i += 1) h = (h + s.charCodeAt(i)) | 0;
  return options[Math.abs(h) % options.length];
}

export function pickVariedSidebarAck(questionId, answerValue) {
  return hashPick(`${questionId}:${answerValue}`, VARIED_SIDEBAR_ACKS);
}

/** Show in the main chat only when the note adds real context (not a repeated filler). */
export function shouldMirrorReactionInThread(text, messages = []) {
  const trimmed = String(text || '').trim();
  if (!trimmed) return false;
  if (GENERIC_INSIGHTS.has(trimmed)) return false;
  if (/^Your readiness is \d+\/100/i.test(trimmed)) return false;
  if (trimmed.length < 36) return false;

  const priorReactions = messages.filter((m) => m.id === '__owen_reaction__');
  if (priorReactions.some((m) => m.text === trimmed)) return false;

  const recent = priorReactions.slice(-2);
  const samePrefix = recent.filter((m) => {
    const a = m.text.slice(0, 24);
    const b = trimmed.slice(0, 24);
    return a === b;
  });
  return samePrefix.length === 0;
}

/**
 * @param {string} questionId
 * @param {Record<string, string>} answers
 * @param {string} [answeredValue] — raw value just submitted
 */
export function getLegacyAnswerReaction(questionId, answers, answeredValue) {
  const v = answeredValue ?? answers[questionId];
  const purpose = answers.purchaseOrRefinance;

  switch (questionId) {
    case 'propertyType': {
      if (v === 'condo') return 'Condo — I will factor HOA and project approval rules.';
      if (v === 'multi') return 'Multi-unit property — rental income may help qualification.';
      if (v === 'manufactured') return 'Manufactured home — I will check foundation and title requirements.';
      return 'Property type noted — that steers which programs are realistic.';
    }
    case 'ruralProperty':
      return v === 'yes'
        ? 'Rural location — USDA and some local programs may be worth a look.'
        : 'Not rural — I will focus on conventional, FHA, and VA paths.';
    case 'existingUSDALoan':
      return v === 'yes'
        ? 'Existing USDA loan — streamline and refinance options may apply.'
        : 'No current USDA loan — other government and conventional paths stay open.';
    case 'priorNADL':
      return v === 'yes'
        ? 'Prior Native American Direct Loan — I will respect NADL-specific rules.'
        : 'No prior NADL — standard agency and conventional options apply.';
    case 'stateHfaParticipant':
      return v === 'yes'
        ? 'State or local assistance interest — I will flag HFA-friendly programs.'
        : 'No HFA path for now — mainstream programs first.';
    case 'tribalHousingPath':
      return v === 'yes'
        ? 'Tribal housing path — specialty programs may be relevant.'
        : 'No tribal housing program — continuing with standard options.';
    case 'affordableProgramInterest':
      return v === 'yes'
        ? 'Affordable homeownership programs — I will include down-payment assistance where it fits.'
        : 'Skipping specialty affordable paths unless something else points there.';
    case 'firstTimeHomeBuyer':
      return v === 'yes'
        ? 'First-time buyer — extra grants and low-down programs may apply.'
        : 'Repeat buyer — I will skip first-time-only incentives.';
    case 'renovationNeeds':
      if (v === 'none') return 'No rehab needed — standard purchase or refi programs stay in play.';
      if (v === 'minor') return 'Light updates — renovation-friendly loans may be worth comparing.';
      if (v === 'major') return 'Major work planned — construction or rehab loans belong in the mix.';
      return 'Renovation plans still open — I will keep flexible program options.';
    case 'agencyLoanServicer':
      if (v === 'fannie') return 'Fannie-backed loan — Refi Now-style options may be on the table.';
      if (v === 'freddie') return 'Freddie-backed loan — similar agency refinance paths may apply.';
      return 'Agency investor unclear — I will not assume Fannie or Freddie specials yet.';
    case 'refiNowLowIncomeHint':
      if (v === 'yes') return 'Moderate income range — agency refinance relief programs may fit.';
      if (v === 'maybe') return 'Income level uncertain — I will keep standard refi paths primary.';
      return 'Above typical area income — standard refinance products are the focus.';
    case 'employmentStatus':
      if (v === 'employed-w2') return 'W-2 employment — straightforward income docs for most lenders.';
      if (v === 'self-employed') return 'Self-employed — bank-statement or tax-return paths may matter.';
      if (v === 'retired') return 'Retired — pension and Social Security documentation will matter.';
      return 'Income type noted — I will match programs to how you earn money.';
    case 'incomeDocumentation':
      if (v === 'bank-statements') return 'Bank-statement income — non-QM or alternative doc may apply.';
      if (v === 'tax-returns') return 'Tax-return income — most agency and conventional paths fit.';
      if (v === 'assets') return 'Asset-based income — specialty programs may be relevant.';
      if (v === 'rental-income') return 'Rental income — investment or DSCR-style options may apply.';
      return 'Income documentation path noted — narrowing lender-friendly options.';
    case 'nonQmFlexInterest':
      return v === 'yes'
        ? 'Including specialty paths for non-traditional income or credit.'
        : 'Keeping mainstream programs — simpler match for now.';
    case 'loanTermPreference':
      if (v === '30') return '30-year term — lower monthly payment, more interest over time.';
      if (v === '15') return '15-year term — higher payment, less interest and faster equity.';
      if (v === 'not-sure') return 'No term preference yet — I will show common 30- and 15-year options.';
      return `${v}-year term noted — balancing payment size and total interest.`;
    case 'rateTypePreference':
      if (v === 'fixed') return 'Fixed rate — payment stays predictable for the life of the loan.';
      if (v === 'arm') return 'Adjustable rate — initial savings possible; later changes are a tradeoff.';
      return 'Open to fixed or adjustable — I will show both where they apply.';
    case 'planToKeepLoan':
      if (v === '10+') return 'Long time in the home — a longer initial fixed period on an ARM may work.';
      if (v === '1-3' || v === '3-5') return 'Shorter stay — a shorter fixed period on an ARM may be worth comparing.';
      return 'Timeline noted — matching initial rate period to how long you may keep the loan.';
    case 'refinanceType':
      if (v === 'va-to-va') return 'VA-to-VA streamline — often the fastest refi path for eligible borrowers.';
      if (v === 'non-va-to-va') return 'Switching to VA — entitlement and funding fee rules apply.';
      if (v === 'cash-out') return 'VA cash-out — equity access with VA guidelines.';
      return 'VA refi goal noted — narrowing streamline vs cash-out paths.';
    case 'annualIncome': {
      const income = parseFloat(String(v || '').replace(/,/g, ''));
      if (income > 0) {
        return `Income around $${income.toLocaleString()} — DTI and program limits come next.`;
      }
      return 'Income noted — I will use it for payment and DTI estimates.';
    }
    default:
      break;
  }

  return pickVariedSidebarAck(questionId, v);
}

export function isGenericInsight(text) {
  return GENERIC_INSIGHTS.has(String(text || '').trim());
}
